import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { apiGet, apiPatch } from "../../lib/api";
import { buildAdminAuthHeaders, isUnauthorizedAdminError, shortMenuItemId } from "./admin-utils";
import {
  buildDraftForSection,
  normalizePizzeriaMenuApiItem,
  SECTION_OPTIONS,
  toPizzeriaMenuDraft,
  toPizzeriaMenuPayload
} from "./pizzeria-menu-admin-utils";
import "../Shop.css";

const AdminMenuItemEditPage = () => {
  const { adminToken, handleUnauthorized } = useOutletContext();
  const navigate = useNavigate();
  const { itemId: itemIdParam } = useParams();
  const itemId = useMemo(() => decodeURIComponent(itemIdParam || ""), [itemIdParam]);
  const [draft, setDraft] = useState(() => buildDraftForSection("PIZZE"));
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const loadItem = useCallback(async () => {
    if (!adminToken || !itemId) {
      setIsLoading(false);
      if (!itemId) {
        setError("ID voce menu non valido.");
      }
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      const payload = await apiGet("/api/admin/pizzeria-menu", {
        headers: buildAdminAuthHeaders(adminToken)
      });

      const items = Array.isArray(payload?.items)
        ? payload.items.map(normalizePizzeriaMenuApiItem).filter(Boolean)
        : [];

      const item = items.find((entry) => entry.id === itemId);
      if (!item) {
        setError("Voce menu non trovata.");
        return;
      }

      setDraft(toPizzeriaMenuDraft(item));
    } catch (loadError) {
      const message = loadError instanceof Error ? loadError.message : "Errore caricamento voce menu";
      if (isUnauthorizedAdminError(loadError)) {
        handleUnauthorized();
        return;
      }
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [adminToken, handleUnauthorized, itemId]);

  useEffect(() => {
    loadItem();
  }, [loadItem]);

  const handleDraftChange = (field, value) => {
    setDraft((current) => {
      if (field !== "section_key") {
        return {
          ...current,
          [field]: value
        };
      }

      const defaults = buildDraftForSection(value);
      return {
        ...current,
        section_key: value,
        subsection_key: value === "PIZZE" ? current.subsection_key || defaults.subsection_key : "",
        price1_label: current.price1_label || defaults.price1_label,
        price2_label: current.price2_label || (value === "PIZZE" ? defaults.price2_label : "")
      };
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!itemId || isSaving) {
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      await apiPatch(`/api/admin/pizzeria-menu/${itemId}`, toPizzeriaMenuPayload(draft), {
        headers: buildAdminAuthHeaders(adminToken)
      });

      navigate("/admin/menu", {
        state: {
          notice: "Voce menu aggiornata."
        }
      });
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : "Errore aggiornamento voce menu";
      if (isUnauthorizedAdminError(saveError)) {
        handleUnauthorized();
        return;
      }
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="admin-section-block admin-edit-page">
      <div className="admin-section-header">
        <div>
          <h3 className="admin-section-title">Modifica voce menu pizzeria</h3>
          <p className="admin-row-meta">
            ID: <strong>{shortMenuItemId(itemId)}</strong>
          </p>
        </div>
        <Link className="shop-back-link" to="/admin/menu">
          Torna al menu
        </Link>
      </div>

      {error && <p className="shop-state shop-error">Errore: {error}</p>}
      {isLoading && <p className="shop-state">Caricamento voce menu...</p>}

      {!isLoading && !error && (
        <form className="admin-menu-create-form" onSubmit={handleSave}>
          <div className="admin-menu-create-grid admin-pizzeria-create-grid">
            <select
              className="admin-form-input"
              value={draft.section_key}
              onChange={(event) => handleDraftChange("section_key", event.target.value)}
            >
              {SECTION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {draft.section_key === "PIZZE" && (
              <select
                className="admin-form-input"
                value={draft.subsection_key}
                onChange={(event) => handleDraftChange("subsection_key", event.target.value)}
              >
                <option value="PIZZE_CLASSICHE">Pizze classiche</option>
                <option value="PIZZE_BIANCHE">Pizze bianche</option>
              </select>
            )}

            <input
              className="admin-form-input"
              type="text"
              placeholder="Nome voce"
              value={draft.name}
              onChange={(event) => handleDraftChange("name", event.target.value)}
              required
            />
            <input
              className="admin-form-input admin-form-input-wide"
              type="text"
              placeholder="Descrizione / ingredienti (opzionale)"
              value={draft.description}
              onChange={(event) => handleDraftChange("description", event.target.value)}
            />

            <div className="admin-pizzeria-price-grid admin-form-input-wide">
              <input
                className="admin-form-input"
                type="text"
                placeholder="Etichetta prezzo 1"
                value={draft.price1_label}
                onChange={(event) => handleDraftChange("price1_label", event.target.value)}
                required
              />
              <input
                className="admin-form-input"
                type="number"
                min="1"
                step="1"
                placeholder="Importo 1 (centesimi)"
                value={draft.price1_amount_cents}
                onChange={(event) => handleDraftChange("price1_amount_cents", event.target.value)}
                required
              />
              <input
                className="admin-form-input"
                type="text"
                placeholder="Etichetta prezzo 2 (opzionale)"
                value={draft.price2_label}
                onChange={(event) => handleDraftChange("price2_label", event.target.value)}
              />
              <input
                className="admin-form-input"
                type="number"
                min="1"
                step="1"
                placeholder="Importo 2 (opzionale)"
                value={draft.price2_amount_cents}
                onChange={(event) => handleDraftChange("price2_amount_cents", event.target.value)}
              />
            </div>

            <input
              className="admin-form-input"
              type="number"
              min="0"
              step="1"
              placeholder="Ordine"
              value={draft.sort_order}
              onChange={(event) => handleDraftChange("sort_order", event.target.value)}
              required
            />

            <label className="admin-checkbox-label">
              <input
                type="checkbox"
                checked={Boolean(draft.active)}
                onChange={(event) => handleDraftChange("active", event.target.checked)}
              />
              Attiva
            </label>
          </div>

          <div className="admin-edit-actions">
            <button type="submit" className="shop-add-button admin-save-button" disabled={isSaving}>
              {isSaving ? "Salvataggio..." : "Salva modifiche"}
            </button>
            <button type="button" className="cart-clear-button" onClick={() => navigate("/admin/menu")}>
              Annulla
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

export default AdminMenuItemEditPage;
